// frontend/src/components/detail/tables/DrawbacksOfSolutionTable.jsx
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, IconButton, Tooltip, Collapse, Link as MuiLink
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { F } from '../../../utils/qualFmt';
import { slugify } from '../../../utils/slug';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// NOTE: no product filter here — Dashboard already passed filtered interviews
function buildGroups(customerStories = []) {
  const groups = new Map(); // drawback_category slug → { category, count, sum, n, instances[] }

  for (const s of customerStories) {
    const arr = Array.isArray(s?.drawbacks_of_solution_choice)
      ? s.drawbacks_of_solution_choice
      : [];
    for (const d of arr) {
      const category = d?.drawback_category || 'Uncategorized';
      const key = slugify(category);

      if (!groups.has(key)) {
        groups.set(key, { category, count: 0, sum: 0, n: 0, instances: [] });
      }
      const g = groups.get(key);
      g.count += 1;

      const imp = toNum(d?.drawback_importance);
      if (imp != null) { g.sum += imp; g.n += 1; }

      g.instances.push({
        name: s.customer_name || 'Interview',
        date: s.conversation_date || '',
        drawback: d?.drawback || '',
        importance: imp,
        justification: d?.drawback_importance_justification || '',
        quote: d?.drawback_importance_quote || '',
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
      });
    }
  }

  return [...groups.values()]
    .map(g => ({
      key: slugify(g.category),
      category: g.category,
      occurrences: g.count,
      avgImportance: g.n ? (g.sum / g.n) : null,
      instances: g.instances.sort((a,b) => (Date.parse(b.date||'')||0) - (Date.parse(a.date||'')||0)),
    }))
    .sort((a,b) => b.occurrences - a.occurrences || a.category.localeCompare(b.category));
}

export default function DrawbacksOfSolutionTable({ customerStories = [] /* productName ignored intentionally */ }) {
  const location = useLocation();
  const rows = React.useMemo(() => buildGroups(customerStories), [customerStories]);

  const [open, setOpen] = React.useState({});
  const toggle = (k) => setOpen(s => ({ ...s, [k]: !s[k] }));

  if (!rows.length) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No drawbacks found for this product.</Typography>;
  }

  return (
    <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell>Drawback</TableCell>
            <TableCell align="right">Occurrences</TableCell>
            <TableCell align="right">Importance (avg)</TableCell>
            <TableCell align="center" width={56}>Open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => {
            const detailsLink = `/drawback/${slugify(r.category)}${location.search || ''}`;
            return (
              <React.Fragment key={r.key}>
                <TableRow hover>
                  <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => toggle(r.key)} aria-label="Expand row">
                      {open[r.key] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.category}</Typography>
                  </TableCell>
                  <TableCell align="right">{r.occurrences}</TableCell>
                  <TableCell align="right">{r.avgImportance != null ? F.drawbackImportance(r.avgImportance).text : '—'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Open drawback details">
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={detailsLink}
                        aria-label="Open drawback details"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Expanded: Interviews with this drawback */}
                <TableRow>
                  <TableCell colSpan={5} style={{ paddingBottom: 0, paddingTop: 0 }}>
                    <Collapse in={!!open[r.key]} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, pl: 6 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Interviews with this drawback</Typography>
                        <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>Drawback (raw)</TableCell>
                              <TableCell align="right">Importance</TableCell>
                              <TableCell>Justification</TableCell>
                              <TableCell>Quote</TableCell>
                              <TableCell align="center" width={56}>Open</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {r.instances.map((inst, i) => (
                              <TableRow key={`${r.key}-inst-${i}`} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{inst.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{inst.date || '—'}</Typography>
                                </TableCell>
                                <TableCell><Typography variant="body2">{inst.drawback || '—'}</Typography></TableCell>
                                <TableCell align="right">{inst.importance != null ? F.drawbackImportance(inst.importance).text : '—'}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{inst.justification || '—'}</Typography>
                                </TableCell>
                                <TableCell>
                                  {inst.quote ? (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                      <OndemandVideoIcon fontSize="small" />
                                      <MuiLink href="#" underline="hover">{inst.quote}</MuiLink>
                                    </Box>
                                  ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Open interview">
                                    <span>
                                      <IconButton size="small" component="a" href={inst.href} target="_blank" rel="noopener" aria-label="Open interview">
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
